import React, { useState, useEffect, useRef } from "react";
import API from "../api/axiosConfig";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import { Gift, Phone, MessageCircle, Mail, ShieldCheck, Loader2 } from "lucide-react";
import CountryPicker from "../components/CountryPicker";
import { COUNTRY_CODES, getCleanPhone } from "../utils/phoneCountryCodes";
import { RECAPTCHA_SITE_KEY as SITE_KEY, storeSessionAndRedirect } from "../utils/authSession";
import { isNativeApp } from "../utils/platform";
import { nativeGoogleSignIn } from "../utils/googleAuth";

function WhatsAppRegisterForm({ nav }) {
  const [step,             setStep]             = useState("form"); // "form" | "code"
  const [name,             setName]             = useState("");
  const [phone,            setPhone]            = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+58");
  const [code,             setCode]             = useState("");
  const [sending,          setSending]          = useState(false);
  const [verifying,        setVerifying]        = useState(false);
  const recaptchaRef = useRef(null);

  const { clean: cleanPhone, expected } = getCleanPhone(phone, phoneCountryCode);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.warning("Ingresá tu nombre");
    if (!cleanPhone || cleanPhone.length !== expected) {
      toast.warning(`El número debe tener exactamente ${expected} dígitos`);
      return;
    }
    const captchaToken = isNativeApp ? undefined : recaptchaRef.current?.getValue();
    if (!isNativeApp && !captchaToken) return toast.warning("Completá el CAPTCHA");

    setSending(true);
    try {
      await API.post("/auth/whatsapp/send-otp", {
        name: name.trim(), phone: cleanPhone, phoneCountryCode, captchaToken,
      });
      toast.success("Te enviamos un código por WhatsApp 📲");
      setCode("");
      setStep("code");
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo enviar el código");
      recaptchaRef.current?.reset();
    } finally { setSending(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.warning("El código debe tener 6 dígitos");
    setVerifying(true);
    try {
      const res = await API.post("/auth/whatsapp/verify-otp", { phone: cleanPhone, phoneCountryCode, code });
      const isNewUser = res.data?.data?.isNewUser ?? res.data?.isNewUser;
      toast.success(isNewUser ? "🎉 Cuenta creada" : "Bienvenido de nuevo 👋");
      await storeSessionAndRedirect(res, nav);
    } catch (err) {
      toast.error(err.response?.data?.message || "Código incorrecto");
    } finally { setVerifying(false); }
  };

  if (step === "code") {
    return (
      <form onSubmit={handleVerify} className="flex flex-col gap-4">
        <p className="text-sm text-gray-500 text-center">
          Ingresá el código de 6 dígitos que enviamos por WhatsApp al{" "}
          <span className="font-semibold text-gray-700">{phoneCountryCode} {cleanPhone}</span>
        </p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="p-3 border border-gray-300 rounded-xl text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-indigo-400 outline-none"
          required
          autoFocus
        />
        <motion.button
          whileTap={{ scale: 0.97 }}
          type="submit"
          disabled={verifying}
          className="flex items-center justify-center gap-2 bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md disabled:opacity-60"
        >
          {verifying ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
          Verificar y continuar
        </motion.button>
        <button
          type="button"
          onClick={() => setStep("form")}
          className="text-xs text-gray-500 hover:underline"
        >
          ← ¿No te llegó? Volver a intentar
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
      <input
        type="text"
        placeholder="Nombre"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none"
        required
      />

      <div>
        <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Número de WhatsApp</label>
        <div className="flex gap-2">
          <CountryPicker value={phoneCountryCode} onChange={(c) => { setPhoneCountryCode(c); setPhone(""); }} />
          <div className="relative flex-1">
            <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="tel"
              placeholder="Número de celular"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl outline-none transition focus:ring-2 focus:ring-indigo-400"
              required
            />
          </div>
        </div>
      </div>

      {!isNativeApp && (
        <div className="flex justify-center pt-1">
          <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY} />
        </div>
      )}

      <motion.button
        whileTap={{ scale: 0.97 }}
        type="submit"
        disabled={sending}
        className="flex items-center justify-center gap-2 bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}
        Enviar código por WhatsApp
      </motion.button>
      <p className="text-xs text-gray-400 text-center">
        Si ya tenés cuenta con este número, vas a ingresar directamente.
      </p>
    </form>
  );
}

export default function Register() {
  const [mode,             setMode]             = useState("whatsapp"); // "whatsapp" | "traditional"
  const [name,             setName]             = useState("");
  const [email,            setEmail]            = useState("");
  const [password,         setPassword]         = useState("");
  const [phone,            setPhone]            = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+58");
  const [referralCode,     setReferralCode]     = useState("");
  const recaptchaRef   = useRef(null);
  const nav            = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-fill referral code from URL ?ref=CODE
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    const { clean: cleanPhone, expected } = getCleanPhone(phone, phoneCountryCode);
    if (!cleanPhone) {
      toast.warning("Ingresa tu número de celular");
      return;
    }
    if (cleanPhone.length !== expected) {
      toast.warning(`El número debe tener exactamente ${expected} dígitos (tiene ${cleanPhone.length})`);
      return;
    }
    const captchaToken = isNativeApp ? undefined : recaptchaRef.current?.getValue();
    if (!isNativeApp && !captchaToken) {
      toast.warning("Completá el CAPTCHA");
      return;
    }
    try {
      const payload = { name, email, password, phone: cleanPhone, phoneCountryCode, role: "user", captchaToken };
      if (referralCode.trim()) payload.referralCode = referralCode.trim();
      await API.post("/auth/register", payload);
      toast.success("✅ Registrado correctamente");
      nav("/login");
    } catch (err) {
      const msg = err.response?.data?.msg || err.response?.data?.message || "";
      if (msg.toLowerCase().includes("phone")) {
        toast.error("Este número de celular ya está registrado");
      } else if (msg.toLowerCase().includes("email")) {
        toast.error("Este correo electrónico ya está registrado");
      } else {
        toast.error(msg || "Error al registrar");
      }
      recaptchaRef.current?.reset();
    }
  };

  const handleGoogleResponse = async (response) => {
    try {
      const res = await API.post("/auth/google", { credential: response.credential });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      toast.success("Bienvenido 👋");
      if (res.data.user.role === "company") nav("/company");
      else if (res.data.user.role === "admin") nav("/admin");
      else nav("/");
    } catch {
      toast.error("Error con Google. Intenta de nuevo.");
    }
  };

  const handleNativeGoogleSignIn = async () => {
    try {
      const idToken = await nativeGoogleSignIn();
      await handleGoogleResponse({ credential: idToken });
    } catch (err) {
      if (!/cancel/i.test(err?.message || "")) {
        toast.error("Error con Google. Intenta de nuevo.");
      }
    }
  };

  useEffect(() => {
    // En la app nativa el botón se maneja aparte, vía Google Sign-In nativo
    // (ver handleNativeGoogleSignIn) — el widget web de Google no funciona
    // embebido en el WebView de Capacitor.
    if (isNativeApp) return;

    // Si ya está cargado reutilizarlo
    if (window.google?.accounts) {
      const btn = document.getElementById("googleBtn");
      if (btn) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        window.google.accounts.id.renderButton(btn, {
          theme: "outline", size: "large", width: "100%", shape: "pill",
        });
      }
      return;
    }
    // Evitar duplicados
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) return;
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google?.accounts) return;
      const btn = document.getElementById("googleBtn");
      if (!btn) return;
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      window.google.accounts.id.renderButton(btn, {
        theme: "outline", size: "large", width: "100%", shape: "pill",
      });
    };
    document.body.appendChild(script);
    // eslint-disable-next-line
  }, [mode]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-supernova-50 to-indigo-100 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white/80 backdrop-blur-md p-8 rounded-2xl shadow-xl w-full max-w-md"
      >
        <h2 className="text-3xl font-bold text-center text-supernova-600 mb-6">
          Crear cuenta
        </h2>

        {/* Selector de modo */}
        <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
          <button
            type="button"
            onClick={() => setMode("whatsapp")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition ${
              mode === "whatsapp" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <MessageCircle size={15} /> WhatsApp rápido
          </button>
          <button
            type="button"
            onClick={() => setMode("traditional")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition ${
              mode === "traditional" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Mail size={15} /> Correo
          </button>
        </div>

        {mode === "whatsapp" ? (
          <WhatsAppRegisterForm nav={nav} />
        ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none"
            required
          />

          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none"
            required
          />

          {/* Teléfono */}
          {(() => {
            const { clean, expected } = getCleanPhone(phone, phoneCountryCode);
            const count = clean.length;
            const isEmpty = count === 0;
            const isValid = count === expected;
            const isOver  = count > expected;
            const country = COUNTRY_CODES.find((c) => c.code === phoneCountryCode);
            const placeholder = phoneCountryCode === "+58" ? "0412 1234567"
              : phoneCountryCode === "+57" ? "3001234567"
              : phoneCountryCode === "+593" ? "0991234567"
              : "Número de celular";
            return (
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Número de celular</label>
                <div className="flex gap-2">
                  <CountryPicker value={phoneCountryCode} onChange={(c) => { setPhoneCountryCode(c); setPhone(""); }} />
                  <div className="relative flex-1">
                    <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      type="tel"
                      placeholder={placeholder}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={`w-full pl-9 pr-14 py-3 border rounded-xl outline-none transition focus:ring-2
                        ${isEmpty    ? "border-gray-300 focus:ring-indigo-400"
                        : isValid    ? "border-green-400 focus:ring-green-300 bg-green-50/30"
                        : isOver     ? "border-red-400 focus:ring-red-300 bg-red-50/30"
                                     : "border-amber-400 focus:ring-amber-300 bg-amber-50/30"}`}
                      required
                    />
                    {!isEmpty && (
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold tabular-nums
                        ${isValid ? "text-green-600" : isOver ? "text-red-500" : "text-amber-500"}`}>
                        {count}/{expected}
                      </span>
                    )}
                  </div>
                </div>
                {!isEmpty && !isValid && (
                  <p className={`text-xs mt-1 pl-1 ${isOver ? "text-red-500" : "text-amber-600"}`}>
                    {isOver
                      ? `Sobran ${count - expected} dígito${count - expected > 1 ? "s" : ""}`
                      : `Faltan ${expected - count} dígito${expected - count > 1 ? "s" : ""}`}
                    {" — "}se esperan {expected} para {country?.name}
                  </p>
                )}
                {isValid && (
                  <p className="text-xs mt-1 pl-1 text-green-600">✓ Número válido</p>
                )}
              </div>
            );
          })()}

          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none"
            required
          />

          {/* Código de referido */}
          <div className="relative">
            <Gift size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-supernova-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Código de referido (opcional)"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className={`w-full pl-9 pr-3 py-3 border rounded-xl font-mono text-sm tracking-widest outline-none focus:ring-2 transition ${
                referralCode
                  ? "border-supernova-300 bg-supernova-50 focus:ring-supernova-300 text-supernova-700"
                  : "border-gray-300 focus:ring-indigo-400"
              }`}
            />
            {referralCode && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-supernova-500 bg-supernova-100 px-2 py-0.5 rounded-full">
                +QPUNTOS
              </span>
            )}
          </div>
          {referralCode && (
            <p className="text-xs text-supernova-600 -mt-2 pl-1">
              🎁 Al registrarte con este código ganarás QPUNTOS de bienvenida
            </p>
          )}

          {!isNativeApp && (
            <div className="flex justify-center pt-1">
              <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY} />
            </div>
          )}

          <motion.button
            whileTap={{ scale: 0.97 }}
            type="submit"
            className="bg-supernova-500 text-white py-3 rounded-xl font-semibold hover:bg-supernova-700 transition-all duration-200 shadow-md"
          >
            Registrarse
          </motion.button>
        </form>
        )}

        {mode === "traditional" && (
          <>
            <div className="flex items-center my-6">
              <div className="flex-grow h-px bg-gray-300" />
              <span className="px-2 text-gray-500 text-sm font-medium">o</span>
              <div className="flex-grow h-px bg-gray-300" />
            </div>

            {isNativeApp ? (
              <button
                type="button"
                onClick={handleNativeGoogleSignIn}
                className="flex items-center justify-center gap-2 border border-gray-300 rounded-full py-2.5 font-semibold text-sm text-gray-700 hover:bg-gray-50 transition"
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                  <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
                </svg>
                Continuar con Google
              </button>
            ) : (
              <div id="googleBtn" className="flex justify-center" />
            )}
          </>
        )}

        <p className="text-center text-sm text-gray-600 mt-4">
          ¿Ya tienes una cuenta?{" "}
          <span onClick={() => nav("/login")} className="text-supernova-600 hover:underline cursor-pointer">
            Inicia sesión
          </span>
        </p>
      </motion.div>
    </div>
  );
}
